document.addEventListener('alpine:init', () => {
    Alpine.data('counterAnimation', (to, template = null, opts = {}) => ({
        // Public options
        to: Number(to) || 0,
        template, // e.g. "###VALUE### km" or null for plain number
        from: opts.from ?? 0,
        duration: Number(opts.duration) || 3000, // ms
        // Internal state
        _decimals: 0,
        _thousands: '',
        _decimalSep: '.',
        _observer: null,
        _hasRun: false,
        // Counting up is decorative: the number the server rendered is already the answer.
        _reduced: window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false,

        init() {
            // infer decimals from 'to' so 12.34 animates with 2 dp
            const s = String(this.to);
            const dot = s.indexOf('.');
            this._decimals = dot >= 0 ? (s.length - dot - 1) : 0;

            // Separators come from data-* attributes set by the template
            // (data-counter-thousands / data-counter-decimal), so the animated number matches
            // the server-rendered value.
            const ds = (this.$root && this.$root.dataset) || {};
            this._thousands = ds.counterThousands || '';
            this._decimalSep = ds.counterDecimal || '.';

            // Keep server-rendered content until we actually start - and with reduced motion
            // never start at all, which leaves that content standing as the final value.
            if (this._reduced) {
                return;
            }

            this._observeFor20px();
        },

        _observeFor20px() {
            this._observer = new IntersectionObserver((entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) continue;
                    const r = entry.intersectionRect;
                    // Ensure at least 20px of BOTH width and height is visible
                    if (r && r.width >= 20 && r.height >= 20) {
                        this._observer.disconnect();
                        this._start();
                        break;
                    }
                }
            }, {
                // multiple thresholds so we quickly detect first intersection
                threshold: [0, 0.01, 0.1, 0.25, 0.5, 1]
            });

            this._observer.observe(this.$root);
        },

        _format(val) {
            const n = this._formatNumber(Number(val), this._decimals);
            return this.template ? this.template.replace('###VALUE###', n) : n;
        },

        // Format with the configured thousands / decimal separators (matches PHP number_format).
        _formatNumber(num, decimals) {
            const parts = num.toFixed(decimals).split('.');
            if (this._thousands) {
                parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, this._thousands);
            }
            return parts.length > 1 ? parts[0] + this._decimalSep + parts[1] : parts[0];
        },

        // nice and simple easeInOutQuad
        _ease(t) { return t < 0.5 ? 2*t*t : 1 - Math.pow(-2*t + 2, 2) / 2; },

        _start() {
            if (this._hasRun) return;
            this._hasRun = true;

            const start = performance.now();
            const from = Number(this.from) || 0;
            const range = this.to - from;

            const tick = (now) => {
                const elapsed = now - start;
                const t = Math.min(1, elapsed / this.duration);
                const eased = this._ease(t);
                const current = from + range * eased;
                this.$root.innerHTML = this._format(current);

                if (t < 1) {
                    requestAnimationFrame(tick);
                } else {
                    // snap to exact final value
                    this.$root.innerHTML = this._format(this.to);
                }
            };

            requestAnimationFrame(tick);
        }
    }));
});
